import {useMemo} from 'preact/hooks';

import {useI18n} from 'utilities/i18n';
import {BrandHandle, DeliveryEstimates} from 'utilities/delivery-promise';
import {useTheme} from 'utilities/color';

import {PipelineProps, Scenario} from '../types';
import {getDefaultScenario} from '../helpers';

import {useShopPromisePipeline} from './shop-promise-pipeline.hook';
import {usePromiseProps} from './promise-props.hook';
import {useOptimusPipeline} from './optimus-pipeline.hook';

function useDefaultScenario() {
  return useMemo(() => getDefaultScenario(), []);
}

export function usePromisePresentment(
  deliveryEstimates: DeliveryEstimates | null,
): Scenario {
  const defaultScenario = useDefaultScenario();
  const i18n = useI18n();
  const promiseProps = usePromiseProps(deliveryEstimates);
  const {theme} = useTheme();

  const pipelineProps: PipelineProps | null = useMemo(
    () =>
      deliveryEstimates &&
      promiseProps && {
        i18n,
        promiseProps,
        deliveryEstimates,
        theme,
      },
    [deliveryEstimates, i18n, promiseProps, theme],
  );

  const shopPromisePipeline = useShopPromisePipeline();
  const optimusPipeline = useOptimusPipeline();

  const pipeline =
    deliveryEstimates?.selectedShippingOption?.brandedPromise?.handle ===
    BrandHandle.Prime
      ? optimusPipeline
      : shopPromisePipeline;

  return useMemo(
    () =>
      deliveryEstimates && pipelineProps
        ? pipeline.reduce(
            (scenario, pipelineFunction) =>
              pipelineFunction(scenario, pipelineProps),
            defaultScenario,
          )
        : defaultScenario,
    [defaultScenario, deliveryEstimates, pipeline, pipelineProps],
  );
}
